import { useState } from 'react';
import toast from 'react-hot-toast';
import { Product } from '../../../../types';

export interface ProductFormData {
  name: string;
  description: string;
  image_url: string;
  stock: number;
  weight: number;
  is_active: boolean;
  is_no_prize: boolean;
}

const initialFormData: ProductFormData = {
  name: '',
  description: '',
  image_url: '',
  stock: 1,
  weight: 1,
  is_active: true,
  is_no_prize: false
};

export const useProductForm = () => {
  const [formData, setFormData] = useState<ProductFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ProductFormData, string>>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof ProductFormData, string>> = {};

    if (!formData?.name?.trim()) {
      errors.name = 'Nome é obrigatório';
    }

    if (formData.stock < 0) {
      errors.stock = 'Estoque deve ser maior ou igual a 0';
    }

    if (formData?.weight < 1) {
      errors.weight = 'Peso deve ser maior que 0';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof ProductFormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return;
    }

    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Tamanho máximo: 5MB');
      return;
    }

    setSelectedFile(file);

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setSelectedFile(null);
    setImagePreview('');
  };

  const setEditingProduct = (product: Product) => {
    setFormData({
      name: product.name,
      description: product.description,
      image_url: product.image_url || '',
      stock: product.stock,
      weight: product.weight,
      is_active: product.is_active,
      is_no_prize: product.is_no_prize || false
    });
    setFormErrors({});
    setSelectedFile(null);
    setImagePreview(product.image_url || '');
  };

  return {
    formData,
    formErrors,
    selectedFile,
    imagePreview,
    validateForm,
    handleInputChange,
    handleFileSelect,
    handleRemoveImage,
    resetForm,
    setEditingProduct
  };
};
